import * as amplitude from '@amplitude/analytics-browser';
import { config } from '@/config';

/** Isolated Amplitude instance for public share-link events — never identify'd. */
const GUEST_INSTANCE_NAME = 'nexus-script-review-guest';

let _initialized = false;
let _guestClient: ReturnType<typeof amplitude.createInstance> | null = null;

function sharedInitOptions(): amplitude.Types.BrowserOptions {
  return {
    defaultTracking: false,
    logLevel: config.amplitudeDebug
      ? amplitude.Types.LogLevel.Verbose
      : amplitude.Types.LogLevel.None,
    serverZone: 'US',
    flushIntervalMillis: 10_000,
    flushQueueSize: 30,
  };
}

function ensureGuestClient(): ReturnType<typeof amplitude.createInstance> | null {
  if (!config.amplitudeApiKey) return null;

  if (!_guestClient) {
    _guestClient = amplitude.createInstance();
    _guestClient.init(config.amplitudeApiKey, {
      ...sharedInitOptions(),
      instanceName: GUEST_INSTANCE_NAME,
    });
  }

  return _guestClient;
}

/**
 * Initialize Amplitude analytics. No-ops if apiKey is empty (local dev).
 * Call once at app boot, after Sentry init.
 */
export function initAnalytics(): void {
  if (!config.amplitudeApiKey) return;

  amplitude.init(config.amplitudeApiKey, sharedInitOptions());
  ensureGuestClient();

  _initialized = true;
}

/**
 * Identify the current user and set user properties.
 */
export function identifyUser(
  userId: string,
  userProperties: Record<string, string | undefined>,
): void {
  if (!_initialized) return;

  amplitude.setUserId(userId);
  const identify = new amplitude.Identify();
  for (const [key, value] of Object.entries(userProperties)) {
    if (value !== undefined) {
      identify.set(key, value);
    }
  }
  amplitude.identify(identify);
}

/**
 * Set the tenant as an Amplitude Group for account-level analysis.
 */
export function setTenantGroup(
  tenantId: string,
  tenantName: string,
  tenantType: string,
): void {
  if (!_initialized) return;

  amplitude.setGroup('tenant', tenantId);

  const groupIdentify = new amplitude.Identify();
  groupIdentify.set('tenant_name', tenantName);
  groupIdentify.set('tenant_type', tenantType);
  amplitude.groupIdentify('tenant', tenantId, groupIdentify);
}

/**
 * Track a named event. Auto-injects tenant_id from the call site.
 * All event names follow Object–Action Title Case (e.g. "Opportunity Set Created").
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  if (!_initialized) return;

  amplitude.track(eventName, properties);
}

/**
 * Track a public-surface event without the session's user/tenant identity.
 * Uses a dedicated Amplitude instance (never identify'd) so guest traffic cannot
 * inherit the logged-in user's userId or tenant group from the main app session.
 */
export function trackAnonymousEvent(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  const guestClient = ensureGuestClient();
  if (!guestClient) return;

  guestClient.track(eventName, properties);
}

/**
 * Reset Amplitude identity on logout. Clears userId and device state.
 */
export function resetAnalytics(): void {
  if (!_initialized) return;

  amplitude.reset();
}
